
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Medication } from "@/types/medication";
import * as htmlToImage from "html-to-image";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Share2, Copy, Download, FileImage, FileText } from "lucide-react";
// Add error boundary for debugging
import { ErrorBoundary } from "@/components/ErrorBoundary";

export interface ShareScheduleButtonProps {
  medications: Medication[] | Record<string, Medication[]>;
  isDemo?: boolean;
  className?: string;
  icon?: React.ReactNode;
  /**
   * Reference to the MedicationSchedule div to capture
   * Allows capturing the actual schedule instead of our internal scheduleRef
   */
  scheduleComponentRef?: React.RefObject<HTMLDivElement>;
  /**
   * Alias for scheduleComponentRef for backwards compatibility
   * @deprecated Use scheduleComponentRef instead
   */
  targetRef?: React.RefObject<HTMLDivElement>;
}

export const ShareScheduleButton = ({
  medications,
  isDemo = false,
  targetRef,
  className,
  icon,
  scheduleComponentRef,
}: ShareScheduleButtonProps) => {
  const { toast } = useToast();
  const scheduleRef = React.useRef<HTMLDivElement>(null);
  const [processedMedications, setProcessedMedications] = useState<Record<string, { morning: Medication[]; afternoon: Medication[]; evening: Medication[]; night: Medication[] }>>({});
  const [hasError, setHasError] = useState<boolean>(false);

  // Define weekDays as a memoized constant to prevent recreation on each render
  const weekDays = useMemo(() => [
    "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
  ], []);
  
  // Memoize medication processing to prevent reprocessing on every render
  // This replaces the useEffect that was causing an infinite loop
  const processedMedicationsData = useMemo(() => {
    try {
      // Guard against null or undefined medications
      if (!medications) {
        return {};
      }
      
      // Handle demo mode special cases
      if (isDemo && Array.isArray(medications) && medications.length === 0) {
        return {};
      }

      // Process the medications based on weekDays
      return weekDays.reduce((acc, day) => {
          let dayMeds: Medication[] = [];
          
          try {
            // In demo mode with array (expected case from demo-patients.ts)
            if (isDemo && Array.isArray(medications)) {
              try {
                // Deep clone to avoid reference issues and validate each medication
                dayMeds = medications.map(med => {
                  if (!med) return null;
                  // Demo medications might not match the exact Medication interface
                  // Ensure all required fields are present with safe defaults
                  const validMed = { 
                    id: med.id || `demo-${Math.random().toString(36).substring(7)}`,
                    name: med.name || 'Demo Medication',
                    form: med.form || 'pill',
                    dosage: med.dosage || '',
                    instructions: med.instructions || '',
                    status: med.status || 'active',
                    timing: med.timing || med.time || 'morning',
                    time: med.time || med.timing || 'morning',
                    taken: !!med.taken,
                    // Include any other properties from the original medication
                    ...med
                  };
                  
                  // Ensure the object is valid for rendering
                  if (!validMed.name) return null;
                  
                  return validMed;
                }).filter(Boolean) as Medication[];
              } catch (error) {
                
                dayMeds = [];
              }
            } 
            // Normal mode with record by day
            else if (!isDemo && typeof medications === 'object' && !Array.isArray(medications)) {
              const dayMedsArray = (medications as Record<string, Medication[]>)[day];
              if (Array.isArray(dayMedsArray)) {
                dayMeds = dayMedsArray.map(med => med ? { ...med } : null).filter(Boolean) as Medication[];
              }
            } 
            // Demo mode with record (alternative format from demo-patients.ts)
            else if (isDemo && typeof medications === 'object' && !Array.isArray(medications)) {
              const allMeds: Medication[] = [];
              // Extract and process all medications from the record
              try {
                Object.values(medications as Record<string, Medication[]>).forEach(medsArray => {
                  if (Array.isArray(medsArray)) {
                    const validMeds = medsArray
                      .map(med => {
                        if (!med) return null;
                        // Ensure each medication has all required fields
                        return {
                          id: med.id || `demo-${Math.random().toString(36).substring(7)}`,
                          name: med.name || 'Demo Medication',
                          form: med.form || 'pill',
                          dosage: med.dosage || '',
                          instructions: med.instructions || '',
                          status: med.status || 'active',
                          timing: med.timing || med.time || 'morning',
                          time: med.time || med.timing || 'morning',
                          taken: !!med.taken,
                          ...med
                        };
                      })
                      .filter(med => med && med.name) as Medication[];
                    allMeds.push(...validMeds);
                  }
                });
              } catch (error) {
                
              }
              dayMeds = allMeds;
            } 
            // Unexpected case - array in normal mode
            else if (Array.isArray(medications)) {
              dayMeds = medications
                .map(med => med ? { ...med } : null)
                .filter(Boolean) as Medication[];
            }
          } catch (e) {
            
            dayMeds = [];
          }

          // Validate each medication object with detailed logging
          dayMeds = (dayMeds || []).filter(med => {
            if (!med) {
              return false;
            }
            
            if (typeof med !== 'object') {
              return false;
            }
            
            // Return true for demo mode to avoid validation errors
            if (isDemo) {
              return true;
            }
            
            // Check required properties with detailed logging for non-demo mode
            const hasId = 'id' in med;
            const hasName = 'name' in med;
            const hasTiming = 'timing' in med || 'time' in med;
            
            return hasId && hasName && hasTiming;
          });

          // Add extra safety when accessing timing/time properties
          const safeMorningFilter = (med: Medication) => {
            try {
              const timing = String(med.timing || '').toLowerCase();
              const time = String(med.time || '').toLowerCase();
              return med && (timing === 'morning' || timing.includes('morning') || timing.includes('am') || 
                             time === 'morning' || time.includes('morning') || time.includes('am'));
            } catch (e) {
              
              return false;
            }
          };
          
          const safeAfternoonFilter = (med: Medication) => {
            try {
              const timing = String(med.timing || '').toLowerCase();
              const time = String(med.time || '').toLowerCase();
              return med && (timing === 'afternoon' || timing.includes('afternoon') || 
                             time === 'afternoon' || time.includes('afternoon'));
            } catch (e) {
              
              return false;
            }
          };
          
          const safeEveningFilter = (med: Medication) => {
            try {
              const timing = String(med.timing || '').toLowerCase();
              const time = String(med.time || '').toLowerCase();
              return med && (timing === 'evening' || timing.includes('evening') || 
                             (timing.includes('pm') && !timing.includes('night')) || 
                             time === 'evening' || time.includes('evening') || 
                             (time.includes('pm') && !time.includes('night')));
            } catch (e) {
              
              return false;
            }
          };
          
          const safeNightFilter = (med: Medication) => {
            try {
              const timing = String(med.timing || '').toLowerCase();
              const time = String(med.time || '').toLowerCase();
              return med && (timing === 'night' || timing.includes('night') || timing.includes('nightly') || 
                             time === 'night' || time.includes('night') || time.includes('nightly'));
            } catch (e) {
              
              return false;
            }
          };

          acc[day] = {
            morning: dayMeds.filter(safeMorningFilter),
            afternoon: dayMeds.filter(safeAfternoonFilter),
            evening: dayMeds.filter(safeEveningFilter),
            night: dayMeds.filter(safeNightFilter)
          };

          return acc;
        }, {} as Record<string, { morning: Medication[]; afternoon: Medication[]; evening: Medication[]; night: Medication[] }>);

    } catch (error) {
      
      setHasError(true);
      return {};
    }
  }, [medications, isDemo, weekDays]); // Only recalculate when these dependencies change
  
  // Set processed medications state once when the memoized value changes
  useEffect(() => {
    setProcessedMedications(processedMedicationsData);
  }, [processedMedicationsData]);

  // Generate a formatted text version of the medication schedule
  const generateTextSchedule = (): string => {
    let text = "MEDICATION SCHEDULE\n\n";

    try {
      // Direct handling for raw medication array (most common use case in the app)
      if (Array.isArray(medications) && medications.length > 0) {
        const directMeds = medications.filter(med => med && med.name);
        if (directMeds.length > 0) {
          text += "YOUR MEDICATIONS:\n";
          directMeds.forEach(med => {
            try {
              if (med.name) {
                const timeOfDay = (med.timing || med.time || 'morning').toUpperCase();
                let medText = `- ${med.name}`;
                if (med.dosage) medText += ` (${med.dosage})`;
                medText += ` [${timeOfDay}]`;
                if (med.instructions) medText += ` - ${med.instructions}`;
                text += medText + "\n";
              }
            } catch (e) {
              
            }
          });
          text += "\n";
          
          // Skip the weekday processing since we've done direct processing
          return text;
        }
      }
      
      // Fall back to weekday processing if direct array handling doesn't work
      // Check if we have any processed medications first
      let hasMedications = false;
      for (const day of weekDays) {
        const dayMeds = processedMedications[day];
        if (dayMeds && (
          (dayMeds.morning && dayMeds.morning.length > 0) ||
          (dayMeds.afternoon && dayMeds.afternoon.length > 0) ||
          (dayMeds.evening && dayMeds.evening.length > 0) ||
          (dayMeds.night && dayMeds.night.length > 0)
        )) {
          hasMedications = true;
          break;
        }
      }
      
      if (!hasMedications) {
        
        // Last resort - try to extract anything from the medications prop
        if (medications) {
          text += "MEDICATIONS (RAW DATA):\n";
          if (Array.isArray(medications)) {
            medications.forEach((med, index) => {
              if (med) {
                text += `- Medication ${index + 1}: ${med.name || 'Unknown'}`;
                if (med.dosage) text += ` (${med.dosage})`;
                if (med.timing || med.time) text += ` [${med.timing || med.time}]`;
                text += "\n";
              }
            });
          } else if (typeof medications === 'object') {
            text += "Medication data available but not in expected format\n";
          }
          text += "\n";
          return text;
        }
      }

      // Standard weekday processing
      weekDays.forEach(day => {
        let hasDayMeds = false;
        
        // Check if this day has any medications
        ['morning', 'afternoon', 'evening', 'night'].forEach(time => {
          if (processedMedications[day]?.[time]?.length > 0) {
            hasDayMeds = true;
          }
        });
        
        // Only add day header if there are medications for this day
        if (hasDayMeds) {
          text += `${day.toUpperCase()}\n`;
          
          const morningMeds = processedMedications[day]?.morning || [];
          if (morningMeds.length > 0) {
            text += "MORNING:\n";
            morningMeds.forEach(med => {
              try {
                if (med && med.name) {
                  let medText = `- ${med.name}`;
                  if (med.dosage) medText += ` (${med.dosage})`;
                  if (med.instructions) medText += ` - ${med.instructions}`;
                  text += medText + "\n";
                }
              } catch (e) {
                
              }
            });
          }
          
          const afternoonMeds = processedMedications[day]?.afternoon || [];
          if (afternoonMeds.length > 0) {
            text += "AFTERNOON:\n";
            afternoonMeds.forEach(med => {
              try {
                if (med && med.name) {
                  let medText = `- ${med.name}`;
                  if (med.dosage) medText += ` (${med.dosage})`;
                  if (med.instructions) medText += ` - ${med.instructions}`;
                  text += medText + "\n";
                }
              } catch (e) {
                
              }
            });
          }
          
          const eveningMeds = processedMedications[day]?.evening || [];
          if (eveningMeds.length > 0) {
            text += "EVENING:\n";
            eveningMeds.forEach(med => {
              try {
                if (med && med.name) {
                  let medText = `- ${med.name}`;
                  if (med.dosage) medText += ` (${med.dosage})`;
                  if (med.instructions) medText += ` - ${med.instructions}`;
                  text += medText + "\n";
                }
              } catch (e) {
                
              }
            });
          }
          
          const nightMeds = processedMedications[day]?.night || [];
          if (nightMeds.length > 0) {
            text += "NIGHT:\n";
            nightMeds.forEach(med => {
              try {
                if (med && med.name) {
                  let medText = `- ${med.name}`;
                  if (med.dosage) medText += ` (${med.dosage})`;
                  if (med.instructions) medText += ` - ${med.instructions}`;
                  text += medText + "\n";
                }
              } catch (e) {
                
              }
            });
          }
          
          text += "\n";
        }
      });

      // Add medication reference with dosages and cycles
      text += "MEDICATION REFERENCE:\n\n";
      uniqueMedications.forEach(med => {
        try {
          if (med && med.name) {
            text += `${med.name}\n`;
            if (med.dosage) text += `- Dosage: ${med.dosage}\n`;
            if (med.instructions) text += `- Instructions: ${med.instructions}\n`;
            if (med.cycle) text += `- Cycle: ${med.cycle}\n`;
            if (med.duration) text += `- Duration: ${med.duration}\n`;
            text += "\n";
          }
        } catch (e) {
          
        }
      });
    } catch (e) {
      
      text += "Error generating schedule. Please try again.\n";
    }

    return text;
  };

  // Copy text version to clipboard
  const copyTextSchedule = async () => {
    try {
      // Generate text content with error handling
      const text = generateTextSchedule();
      if (!text || text.trim() === "") {
        throw new Error("Generated text is empty");
      }
      
      // Check if clipboard API is available
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(text);
          toast({
            title: "Copied to clipboard",
            description: "Text version of medication schedule copied",
          });
        } catch (clipboardError) {
          // Handle permission issues or other clipboard API errors
          
          toast({
            title: "Permission denied",
            description: "Could not access clipboard. Check browser permissions.",
            variant: "destructive",
          });
        }
      } else {
        // Fallback for browsers without clipboard API
        
        toast({
          title: "Not supported",
          description: "Clipboard copying not supported in this browser",
          variant: "destructive",
        });
      }
    } catch (error) {
      
      toast({
        title: "Error",
        description: "Failed to copy schedule text",
        variant: "destructive",
      });
    }
  };

  // Generate image from either the passed scheduleComponentRef or our internal ref
  const generateImage = async (format: 'png' | 'pdf'): Promise<Blob | null> => {
    try {
      // Use the external component ref if provided, otherwise use our internal ref
      const targetRef = scheduleComponentRef?.current || scheduleRef.current;
      
      if (!targetRef) {
        
        return null;
      }

      let blob: Blob | null = null;
      
      // If in demo mode, try to use the static demo image
      if (isDemo) {
        try {
          const response = await fetch('/demo-schedule.png');
          if (response.ok) {
            blob = await response.blob();
            return blob;
          } else {
            // Fall back to DOM capture if demo image fails
            blob = null;
          }
        } catch (error) {
          // Fall back to DOM capture if demo image fails
          blob = null;
        }
      } 
      
      // Make the target visible temporarily for capture
      const originalVisibility = targetRef.style.visibility;
      targetRef.style.visibility = "visible";
    
      try {
        if (format === 'png') {
          blob = await htmlToImage.toBlob(targetRef, {
            backgroundColor: "#ffffff",
            quality: 0.95,
            skipFonts: true,
            width: 1200,
            style: {
              fontFamily: "system-ui, -apple-system, sans-serif",
            },
          });
        } else if (format === 'pdf') {
          // For PDF, we first create a PNG and then convert it to PDF
          // We're using a simpler approach here - in a production app you might want
          // to use a library like jsPDF for better quality PDFs
          const dataUrl = await htmlToImage.toPng(targetRef, {
            backgroundColor: "#ffffff",
            quality: 0.95,
            skipFonts: true,
            width: 1200,
            style: {
              fontFamily: "system-ui, -apple-system, sans-serif",
            },
          });
          
          // Convert the data URL to a Blob
          const response = await fetch(dataUrl);
          blob = await response.blob();
        }
      } catch (error) {
        
        return null;
      } finally {
        // Always restore original visibility
        try {
          targetRef.style.visibility = originalVisibility;
        } catch (e) {
          
        }
      }
      
      return blob;
    } catch (error) {
      
      return null;
    }
  };

  // Handle sharing via platform share API if available
  const handleShare = async () => {
    if (!scheduleComponentRef?.current && !scheduleRef.current) {
      
      toast({
        title: "Error",
        description: "No schedule to capture. Please try again.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Debug logging removed to conserve browser memory
      
      const blob = await generateImage('png');
      if (!blob) {
        
        toast({
          title: "Error",
          description: "Could not generate schedule image",
          variant: "destructive",
        });
        return;
      }
      
      // Format filename with consistent date format
      const formattedDate = new Date().toISOString().split('T')[0];
      const fileName = `medication-schedule-${formattedDate}.png`;
      
      try {
        // Check if Web Share API is available and can share files
        const file = new File([blob], fileName, { type: "image/png" });
        const canShareFiles = navigator.share && 
                            typeof navigator.canShare === 'function' && 
                            navigator.canShare({ files: [file] });
                            
        if (canShareFiles) {
          // Use the Web Share API
          await navigator.share({
            files: [file],
            title: "Medication Schedule",
            text: `Medication schedule for ${formattedDate}`,
          });
          
          toast({
            title: "Success",
            description: "Schedule shared successfully",
          });
        } else {
          // Fallback for browsers without sharing capability
          
          downloadImage('png');
          toast({
            title: "Download started",
            description: "Sharing not available on this device, downloading instead",
          });
        }
      } catch (shareError) {
        
        
        // Don't show error toast if user simply cancelled the share
        if (shareError instanceof Error && shareError.name === "AbortError") {
          
          return;
        }
        
        // Fall back to download if sharing fails
        downloadImage('png');
        toast({
          title: "Download started",
          description: "Sharing failed, downloading image instead",
        });
      }
    } catch (error) {
      
      toast({
        title: "Error",
        description: "Failed to share schedule image",
        variant: "destructive",
      });
    }
  };
  
  // Download the image in specified format
  const downloadImage = async (format: 'png' | 'pdf') => {
    try {
      const blob = await generateImage(format);
      if (!blob) {
        
        toast({
          title: "Error",
          description: `Could not generate ${format.toUpperCase()} file`,
          variant: "destructive",
        });
        return;
      }
      
      // Use consistent date format for filenames
      const formattedDate = new Date().toISOString().split('T')[0];
      const extension = format;
      const mimeType = format === 'png' ? 'image/png' : 'application/pdf';
      const fileName = `medication-schedule-${formattedDate}.${extension}`;
      
      try {
        // Create and trigger download
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        
        // Add to DOM, click, and clean up
        document.body.appendChild(link);
        link.click();
        
        // Use setTimeout to ensure the browser has time to process the download
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);
        
        toast({
          title: "Downloaded",
          description: `Schedule downloaded as ${format.toUpperCase()}`,
        });
      } catch (downloadError) {
        
        toast({
          title: "Error",
          description: `Download operation failed`,
          variant: "destructive",
        });
      }
    } catch (error) {
      
      toast({
        title: "Error",
        description: `Failed to download schedule as ${format.toUpperCase()}`,
        variant: "destructive",
      });
    }
  };

  // Safely extract all medications with proper type handling
  const allMedications = React.useMemo(() => {
    if (!medications) return [];
    
    try {
      // First, handle the case when medications is a direct array (most common in the actual app)
      if (Array.isArray(medications)) {
        return medications.filter(Boolean);
      } 
      // Handle the case where it's a record by day
      else if (typeof medications === 'object' && !Array.isArray(medications)) {
        return Object.values(medications as Record<string, Medication[]>)
          .flat()
          .filter(Boolean);
      } 
      // Handle any other unexpected format
      else {
        return [];
      }
    } catch (e) {
      
      return [];
    }
  }, [medications]);

  // Safely extract unique medications with full type safety
  const uniqueMedications = React.useMemo(() => {
    try {
      if (!allMedications || !Array.isArray(allMedications) || allMedications.length === 0) {
        return [];
      }
      
      return Array.from(new Set(
        allMedications
          .filter(med => med && typeof med === 'object' && 'name' in med && med.name)
          .map(med => med.name)
      ))
        .map(name => allMedications.find(med => med && med.name === name))
        .filter((med): med is Medication => med !== undefined) as Medication[];
    } catch (e) {
      
      return [];
    }
  }, [allMedications]);

  // If we detected an error processing medications, render a simple button that shows error toast
  if (hasError) {
    return (
      <div className={className}>
        <Button 
          className={`${className} bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 gap-2`}
          onClick={() => {
            toast({
              title: "Error",
              description: "Unable to share schedule due to data processing issues",
              variant: "destructive",
            });
          }}
        >
          {icon || <Share2 className="h-4 w-4" />} Share Schedule
        </Button>
      </div>
    );
  }

  // For the normal case with valid data, render the dropdown
  return (
    <div className={className}>
      {/* Wrap content with a custom error handler instead of ErrorBoundary component */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              className={className || 'h-9 w-9 rounded-full flex items-center justify-center bg-gray-50 dark:bg-gray-800'}
              variant="ghost"
              size="icon"
              aria-label="Share medication schedule"
            >
              {icon || <Share2 className="h-5 w-5" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleShare}>
              <Share2 className="mr-2 h-4 w-4" />
              Share Image
            </DropdownMenuItem>
            <DropdownMenuItem onClick={copyTextSchedule}>
              <Copy className="mr-2 h-4 w-4" />
              Copy Text
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadImage('png')}>
              <FileImage className="mr-2 h-4 w-4" />
              Download PNG
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadImage('pdf')}>
              <FileText className="mr-2 h-4 w-4" />
              Download PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div
          ref={scheduleRef}
          className="fixed left-0 top-0 -z-50 p-8 bg-white"
          style={{
            visibility: "hidden",
            position: "absolute",
            width: "1200px",
            minHeight: "600px",
            pointerEvents: "none",
            opacity: 0
          }}
          aria-hidden="true"
        >
          <div className="flex items-center gap-4 mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              Medication Schedule
            </h1>
          </div>

          <div className="space-y-4">
            {weekDays.map((day) => (
              <div key={day} className="bg-white rounded-lg p-4 border border-gray-200">
                <h3 className="font-semibold text-purple-900">{day}</h3>
                {["morning", "afternoon", "evening", "night"].map((timeOfDay) => {
                  // Safely access medications with explicit type checking
                  const timeKey = timeOfDay as "morning" | "afternoon" | "evening" | "night";
                  const dayMeds = processedMedications[day];
                  const meds = dayMeds && dayMeds[timeKey] ? dayMeds[timeKey] : [];
                  
                  // Skip rendering sections with no medications (cleaner UI)
                  if (meds.length === 0) return null;
                  
                  return (
                    <div key={`${day}-${timeOfDay}`} className="mb-3 ml-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={
                          timeOfDay === "morning" ? "text-amber-600" : 
                          timeOfDay === "afternoon" ? "text-orange-500" :
                          timeOfDay === "evening" ? "text-blue-600" :
                          "text-indigo-700"
                        }>
                          {timeOfDay === "morning" ? "☀️" : 
                           timeOfDay === "afternoon" ? "🕑" : 
                           timeOfDay === "evening" ? "🌆" : 
                           "🌙"}
                        </span>
                        <span className="text-sm font-medium">
                          {timeOfDay.charAt(0).toUpperCase() + timeOfDay.slice(1)}
                        </span>
                      </div>
                      <div className="ml-6 space-y-2">
                        {meds.length > 0 ? meds.map((med, idx) => (
                          <div key={`${day}-${timeOfDay}-${idx}`} className="text-sm">
                            {med.name} - {med.dosage}
                            {med.instructions && (
                              <span className="text-gray-600 ml-2">({med.instructions})</span>
                            )}
                          </div>
                        )) : (
                          <div className="text-sm text-gray-500 italic">No medications</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4">Medication Reference</h2>
            <div className="grid grid-cols-2 gap-4">
              {uniqueMedications.map((med) => (
                <div key={med.name} className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-semibold">{med.name}</h3>
                  <p className="text-sm text-gray-600">Dosage: {med.dosage}</p>
                  {med.instructions && (
                    <p className="text-sm text-gray-600">Instructions: {med.instructions}</p>
                  )}
                  {med.duration && (
                    <p className="text-sm text-gray-600">Duration: {med.duration}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-xs mt-6 text-gray-500">
            Generated on {new Date().toLocaleDateString()}
          </div>
        </div>
    </div>
  );
};

// Create an error-bounded wrapper component for production use
export const ShareScheduleButtonWithErrorHandling: React.FC<ShareScheduleButtonProps> = (props) => {
  // Use the same toast hook that ShareScheduleButton uses
  const { toast } = useToast();
  const [hasError, setHasError] = useState<boolean>(false);
  
  // Reset error state when props change, especially for demo mode
  useEffect(() => {
    setHasError(false);
  }, [props.medications, props.isDemo]);
  
  // Handle any errors during rendering
  if (hasError) {
    return (
      <Button 
        className={`${props.className || ''} bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 gap-2`}
        onClick={() => {
          toast({
            title: "Error",
            description: "Share functionality currently unavailable",
            variant: "destructive",
          });
        }}
      >
        {props.icon || <Share2 className="h-4 w-4" />} Share Schedule
      </Button>
    );
  }
  
  try {
    return (
      <ErrorBoundary>
        <ShareScheduleButton {...props} />
      </ErrorBoundary>
    );
  } catch (error) {
    
    setHasError(true);
    return (
      <Button 
        className={`${props.className || ''} bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 gap-2`}
        onClick={() => {
          toast({
            title: "Error",
            description: "Share functionality currently unavailable",
            variant: "destructive",
          });
        }}
      >
        {props.icon || <Share2 className="h-4 w-4" />} Share Schedule
      </Button>
    );
  }
};

// Export the error-bounded version as default for production use
export default ShareScheduleButtonWithErrorHandling;
